<template>
  <div class="px-8 py-2">
    <v-container
      v-if="showImportPartySelector"
      fluid
    >
      <v-row>
        <v-col cols="12">
          <v-switch
            v-model="partyData.selected"
            label="Import this Party from Oregon Worker Relief Fund"
          />
        </v-col>
      </v-row>
    </v-container>

    <v-form
      :disabled="!partyData.selected"
    >
      <div class="d-flex align-center text-overline">
        <v-divider />
        <div class="px-4">
          Personal Details
        </div>
        <v-divider />
      </div>

      <v-container fluid>
        <v-row>
          <v-col cols="4">
            <v-text-field
              v-model="partyData.first_name"
              label="First Name *"
              outlined
              :error-messages="getErrorMessages($v.partyData.first_name)"
              @input="_ => triggerValidations()"
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="partyData.middle_name"
              label="Middle Name"
              outlined
              :error-messages="getErrorMessages($v.partyData.middle_name)"
              @input="_ => triggerValidations()"
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="partyData.last_name"
              label="Last Name *"
              outlined
              :error-messages="getErrorMessages($v.partyData.last_name)"
              @input="_ => triggerValidations()"
            />
          </v-col>
          <BirthDateField
            class="col-4 pa-4"
            :date="partyData.dob"
            :disabled="!partyData.selected"
            outlined
            required
            :uncertain="partyData.uncertain_dob === true"
            :error-messages="getErrorMessages($v.partyData.dob)"
            @update-date="newValue => updateBirthdateField(newValue)"
            @update-box="newValue => updateBirthdateBoxField(newValue)"
            @valid-changed="isValid => setBirthDateValidation(isValid)"
          />
          <v-col cols="4">
            <v-autocomplete
              v-model="partyData.birth_country_id"
              :hint="birthCountryHint"
              :items="countryOptions"
              item-text="name"
              item-value="id"
              label="Country of Birth"
              outlined
              persistent-hint
              :error-messages="getErrorMessages($v.partyData.birth_country_id)"
              @input="_ => triggerValidations()"
            />
          </v-col>
          <v-col cols="4">
            <v-autocomplete
              v-model="partyData.gender_id"
              :items="genderOptions"
              item-text="name"
              item-value="id"
              label="Gender *"
              outlined
              :error-messages="getErrorMessages($v.partyData.gender_id)"
              @input="_ => triggerValidations()"
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="partyData.a_number"
              v-mask="aNumMask"
              label="A#"
              outlined
              :error-messages="getErrorMessages($v.partyData.a_number)"
              @input="_ => triggerValidations()"
            />
          </v-col>
          <v-col cols="4">
            <ComboDate
              class="mt-3 ml-1"
              label="Last Entry US"
              :date="partyData.date_last_entry"
              :uncertain="partyData.date_last_entry_uncertain"
              required
              outlined
              :disabled="!partyData.selected"
              for-party-edit
              :error-messages="getErrorMessages($v.partyData.date_last_entry)"
              @update-box="value => onDateLastEntryUncertainUpdate(value)"
              @update-date="value => onDateLastEntryUpdate(value)"
              @tab-out="_ => triggerValidations()"
            />
          </v-col>
        </v-row>
      </v-container>

      <div
        v-if="partyData.learned_languages.length"
        class="d-flex align-center text-overline"
      >
        <v-divider />
        <div class="px-4">
          Languages
        </div>
        <v-divider />
      </div>

      <v-container
        v-if="partyData.learned_languages.length"
        fluid
      >
        <v-row>
          <v-col
            v-for="(language, index) of partyData.learned_languages"
            :key="index"
            class="d-flex"
            cols="6"
          >
            <v-autocomplete
              v-model="language.language_id"
              item-text="name"
              item-value="id"
              :items="languageOptions"
              label="Language *"
              outlined
              :error-messages="getErrorMessages($v.partyData.learned_languages, index, 'language_id')"
              @input="_ => triggerValidations()"
            />
            <v-checkbox
              v-model="language.primary"
              class="pl-4 mt-3"
              label="Primary"
              @click="_ => setPrimaryLanguage(language.language_id)"
            />
          </v-col>
        </v-row>
      </v-container>

      <div class="d-flex align-center text-overline">
        <v-divider />
        <div class="px-4">
          Phone Number
        </div>
        <v-divider />
      </div>

      <PhoneFields
        v-model="partyData.phone"
        :required="false"
      />

      <div class="d-flex align-center text-overline">
        <v-divider />
        <div class="px-4">
          E-mail Address
        </div>
        <v-divider />
      </div>

      <EmailFields
        v-model="partyData.email"
        :required="false"
      />

      <div class="d-flex align-center text-overline">
        <v-divider />
        <div class="px-4">
          Physical Address
        </div>
        <v-divider />
      </div>

      <ZipableAddressFields
        v-model="physicalAddress"
        class="mb-0"
        :locked="false"
      />

      <div class="d-flex align-center text-overline">
        <v-divider />
        <div class="px-4">
          Mailing Address
        </div>
        <v-divider />
      </div>

      <v-container fluid>
        <v-row>
          <v-col>
            <v-switch
              v-model="mailingAddress.same_as_physical"
              class="mt-0"
              dense
              hide-details
              label="Same as Physical Address"
            />
          </v-col>
        </v-row>
      </v-container>

      <v-form
        :disabled="mailingAddress.same_as_physical"
      >
        <ZipableAddressFields
          v-model="mailingAddress"
          class="mb-0"
          :locked="false"
        />
      </v-form>

      <div class="d-flex align-center text-overline">
        <v-divider />
        <div class="px-4">
          Documents
        </div>
        <v-divider />
      </div>

      <v-container fluid>
        <v-row v-if="partyData.documents.length">
          <v-col>
            <v-checkbox
              class="mt-0 ml-4"
              :disabled="!partyData.selected"
              hide-details
              :indeterminate="someDocumentsSelected"
              :input-value="allDocumentsSelected"
              label="Select all documents"
              @click="_ => toggleDocumentsSelection()"
            />
          </v-col>
        </v-row>
        <LegalCaseEditorImportPartyEditDocument
          v-for="(document, index) of partyData.documents"
          :key="`nav-${partyData.identifier}-doc-${document.external_id}-${index}`"
          v-model="partyData.documents[index]"
          :disabled="!partyData.selected"
          :document-types="documentTypes"
          @preview-document="id => openExternalDocumentPreview(partyData.external_id, id, document.original_name)"
          @document-name-changed="name => onDocumentNameChanged(index, name)"
        />
        <v-row v-if="partyData.documents.length && showDocumentNameSharingOption">
          <v-col>
            <v-switch
              v-model="documentNameSharingEnabled"
              label="Use same document names for all parties"
            />
          </v-col>
        </v-row>
      </v-container>

      <div class="d-flex align-center text-overline">
        <v-divider />
        <div class="px-4">
          Other Information
        </div>
        <v-divider />
      </div>

      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="partyData.narratives[0].text"
              auto-grow
              label="Notes"
              outlined
            />
          </v-col>
        </v-row>
      </v-container>
    </v-form>

    <v-dialog
      v-model="documentPreview.isOpen"
      persistent
      width="auto"
    >
      <v-card min-width="400">
        <v-card-title>
          Document Preview
        </v-card-title>
        <v-card-text class="flex align-center justify-center">
          <v-img
            v-if="documentPreview.data"
            contain
            max-height="70vh"
            max-width="80vw"
            :src="documentPreview.data"
          />
          <div
            v-else
            class="mt-12 mb-8"
          >
            <v-row
              class="fill-height ma-0"
              align="center"
              justify="center"
            >
              <div
                class="text-center"
              >
                <v-progress-circular
                  color="primary"
                  indeterminate
                  size="64"
                />
              </div>
            </v-row>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            text
            @click="_ => openExternalDocument(documentPreview.navigationId, documentPreview.documentId, documentPreview.filename)"
          >
            Download
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            @click="documentPreview.isOpen = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { helpers, requiredIf } from '@vuelidate/validators'
import { mapActions, mapGetters } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { MASKS, openFileContentsInBrowser } from '@/utilities'
import { alertHandler } from '@/alertHandler'
import { parseErrorResponseMessage } from '@/utilities/error-responses'

import { INTEGRATION_NAMESPACE } from '@/store/integrations'
import { SHARED_NAMESPACE } from '@/store/shared'

import BirthDateField from '@/components/Fields/BirthDate.vue'
import ComboDate from '@/components/Fields/ComboDate.vue'
import EmailFields from '@/components/Party/PartyEdit/SectionCards/PersonalDataCards/EmailFields.vue'
import LegalCaseEditorImportPartyEditDocument from './LegalCaseEditorImportPartyEditDocument.vue'
import PhoneFields from '@/components/Party/PartyEdit/SectionCards/PersonalDataCards/PhoneFields.vue'
import ZipableAddressFields from '@/components/Party/PartyEdit/SectionCards/PersonalDataCards/ZipableAddressFields.vue'

export default {
  name: 'LegalCaseEditorImportPartyEdit',

  components: {
    BirthDateField,
    ComboDate,
    EmailFields,
    LegalCaseEditorImportPartyEditDocument,
    PhoneFields,
    ZipableAddressFields,
  },

  model: {
    prop: 'party',
    event: 'change',
  },

  props: {
    enableDocumentNameSharing: {
      type: Boolean,
      default: false,
    },

    party: {
      type: Object,
      required: true,
    },

    showDocumentNameSharingOption: {
      type: Boolean,
      default: true,
    },

    showImportPartySelector: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
      $v: useVuelidate(),
    }
  },

  data () {
    return {
      aNumMask: MASKS.ANUMBER,
      birthDateValid: false,

      documentPreview: {
        data: null,
        documentId: null,
        filename: null,
        isOpen: false,
        navigationId: null,
      },

      documentTypes: [
        { text: 'Birth Certificate', value: 'birth-certificate' },
        { text: 'Death Certificate', value: 'death-certificate' },
        { text: 'National or State ID', value: 'national-or-state-id' },
        { text: 'Name Change Document', value: 'name-change-document' },
        { text: 'Immigration Status Document', value: 'immigration-status-document' },
        { text: 'Other', value: 'other' },
      ],
    }
  },

  computed: {
    allDocumentsSelected () {
      const selectedCount = this.partyData.documents.filter(doc => doc.selected).length
      return selectedCount > 0 && selectedCount === this.partyData.documents.length
    },

    birthCountryHint () {
      return !this.partyData.birth_country_id && this.partyData.birth_country
        ? `Unmactched value: ${this.partyData.birth_country}`
        : null
    },

    countryOptions () {
      return this.getCachedDropdown('countries')
    },

    documentNameSharingEnabled: {
      get () { return this.enableDocumentNameSharing },

      set (value) {
        this.$emit('document-name-sharing-changed', { documents: this.partyData.documents, enabled: value })
      },
    },

    genderOptions () {
      return this.getCachedDropdown('genders')
    },

    languageOptions () {
      return this.getCachedDropdown('languages')
    },

    mailingAddress () {
      return this.partyData.addresses.find(address => address.address_type === 'mailing')
    },

    partyData () {
      return this.party
    },

    physicalAddress () {
      return this.partyData.addresses.find(address => address.address_type === 'physical')
    },

    someDocumentsSelected () {
      const selectedCount = this.partyData.documents.filter(doc => doc.selected).length
      return selectedCount > 0 && selectedCount !== this.partyData.documents.length
    },

    usaCountryId () {
      return this.countryOptions.find(entry => entry.name.trim().toLowerCase() === 'usa')?.id
    },

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    'mailingAddress.same_as_physical': {
      handler (newVal, oldVal) {
        if (newVal !== oldVal && newVal) this.updateMailingAddressWithPhysicalAddress()
      },

      immediate: true,
    },

    physicalAddress: {
      deep: true,

      handler (newVal) {
        if (!newVal.same_as_physical) return
        this.updateMailingAddressWithPhysicalAddress()
      },

      immediate: true,
    },
  },

  validations () {
    return {
      partyData: {
        a_number: {
          format: helpers.withMessage(
            'Invalid A-number',
            value => !value || /^[\d]{9}$/.test((value || '').replaceAll('-', ''))
          ),
        },

        birth_country_id: {},
        date_last_entry: {},
        date_last_entry_uncertain: {},
        dob: { required: requiredIf(this.partyData.selected && !this.partyData.uncertain_dob) },

        first_name: { required: requiredIf(this.partyData.selected) },
        gender_id: { required: requiredIf(this.partyData.selected) },
        last_name: { required: requiredIf(this.partyData.selected) },

        learned_languages: {
          $each: helpers.forEach({
            language_id: { required: requiredIf(this.partyData.selected) },
            primary: {},
          }),
        },

        middle_name: {},
        uncertain_dob: {},
      },
    }
  },

  async created () {
    await this.getDropdownOptions({ options: false, path: 'countries' })
    await this.getDropdownOptions({ options: false, path: 'genders' })
    await this.getDropdownOptions({ options: false, path: 'languages' })
  },

  methods: {
    getErrorMessages (validator, index, field) {
      if (!index && !field) return validator?.$errors?.map(e => e.$message)

      return validator?.$each.$response.$errors[index][field]?.map(e => e.$message)
    },

    onDateLastEntryUncertainUpdate (value) {
      this.partyData.date_last_entry_uncertain = value
      this.triggerValidations()
    },

    onDateLastEntryUpdate (value) {
      this.partyData.date_last_entry = value
      this.triggerValidations()
    },

    onDocumentNameChanged (index, name) {
      this.$emit('document-name-changed', { index, name })
    },

    openExternalDocument (navigationId, documentId, filename) {
      this.getOWRNavigationDocument({ documentId, navigationId }).
        then(data => {
          try {
            openFileContentsInBrowser(data, 'application/octet-stream', true, filename)
          } catch (error) {
            this.displayAlert('error', 'There was an error opening the file: ' + error)
          }
        }).
        catch(error => this.displayAlert('error', parseErrorResponseMessage(error)))
    },

    openExternalDocumentPreview (navigationId, documentId, filename) {
      this.documentPreview.navigationId = navigationId
      this.documentPreview.documentId = documentId
      this.documentPreview.filename = filename
      this.documentPreview.data = null

      this.documentPreview.isOpen = true

      this.getOWRNavigationDocumentPreview({ documentId, navigationId }).
        then(data => {
          try {
            const fileReader = new FileReader()
            fileReader.readAsDataURL(data)

            fileReader.onloadend = () => {
              this.documentPreview.data = fileReader.result
            }
          } catch (error) {
            this.displayAlert('error', 'There was an error opening the file: ' + error)
            this.documentPreview.isOpen = false
          }
        }).
        catch(error => this.displayAlert('error', parseErrorResponseMessage(error)))
    },

    setBirthDateValidation (isValid) {
      this.birthDateValid = isValid
    },

    setPrimaryLanguage (languageId) {
      for (const language of this.partyData.learned_languages) {
        if (language.language_id !== languageId) language.primary = false
      }
      this.triggerValidations()
    },

    toggleDocumentsSelection () {
      const newState = !this.allDocumentsSelected
      this.partyData.documents.forEach(doc => { doc.selected = newState })
    },

    triggerValidations () {
      this.$v.$validate()
    },

    updateBirthdateBoxField (newVal) {
      this.partyData.uncertain_dob = newVal
      this.triggerValidations()
    },

    updateBirthdateField (newDate) {
      this.partyData.dob = newDate
      this.triggerValidations()
    },

    updateMailingAddressWithPhysicalAddress () {
      this.mailingAddress.address = this.physicalAddress.address
      this.mailingAddress.city = this.physicalAddress.city
      this.mailingAddress.country_id = this.physicalAddress.country_id
      this.mailingAddress.county = this.physicalAddress.county
      this.mailingAddress.state = this.physicalAddress.state
      this.mailingAddress.zip = this.physicalAddress.zip
    },

    ...mapActions(INTEGRATION_NAMESPACE, [
      'getOWRNavigationDocument',
      'getOWRNavigationDocumentPreview',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
